import { useEffect, useState } from "react";
import { useParams }           from "react-router";
import { Link }                from "react-router-dom";
import { Tooltip }             from "react-tooltip";

/* Project References */
import ClaimStatusBadge   from "../../../components/ClaimStatusBadge";
import RejectReasonDialog from "../../../components/RejectReasonDialog";
import SkeletonCardLoad   from "../../../components/SkeletonCardLoad";
import msalRestClient     from "../../../services/clients/MsalRestClient";
import { UpdateClaim, Claim, ClaimStatus, RejectReason } from "../../../models/Claim";
import { useMsal }           from "@azure/msal-react";
import { MsalContextHelper } from "../../../utils/MsalHelper";
import { AuditEvent }        from "../../../models/ClaimAudit";
import ResendDialog          from "../../../components/ResendDialog";
import { AxiosResponse }     from "axios";
import { SharePointFileMetadata } from "../../../models/SharePointFileMetadata";
import DeleteDialog               from "../../../components/DeleteDialog";

const TravelClaim = () => {

    const { guid }     = useParams();
    const { instance } = useMsal();

    const msalHelper = new MsalContextHelper(instance);

    let claimInitState: UpdateClaim = {
        guid: guid ?? "",
        confirmed: true
    }

    const [isLoading, setIsLoading]           = useState<boolean>(false);
    const [versionDetails, setVersionDetails] = useState<boolean>(false);

    const [claim, setClaim] = useState<Claim>();
    const [files, setFiles] = useState<SharePointFileMetadata[]>([]);
    const [putClaim] = useState<UpdateClaim>(claimInitState);

    useEffect(() => {

        setIsLoading(true);

        Promise.all([
            msalRestClient.get<Claim>("/api/travelclaim/" + guid),
            msalRestClient.get<SharePointFileMetadata[]>("/api/elvdata/files/" + guid)
        ])
            .then(response => onApiResponse(response))
            .catch(err => {
                // TO-DO: Implement Proper Error Handling ...
            })
            .finally(() => setIsLoading(false));

    }, [guid])

    /**
     * 
     * @param param0
     */
    const onApiResponse = ([claimResponse, fileReponse]: [AxiosResponse<Claim, any>, AxiosResponse<SharePointFileMetadata[], any>]) => {

        if (claimResponse.status === 200) { setClaim(claimResponse.data); }

        if (fileReponse.status === 200) { setFiles(fileReponse.data); }
        else if (fileReponse.status === 204) { setFiles([]); }
    }

    /**
     * Event Handler for pressing Approval Button. 
     * Changes claim status to "Approval Run". 
     */
    function onDecline(reason: RejectReason, othertxt: string): void
    {
        putClaim.status       = ClaimStatus.Rejected;
        putClaim.rejectReason = reason;
        putClaim.othertxt     = othertxt;

        setIsLoading(true);
        msalRestClient.put<Claim>("/api/travelclaim", putClaim)
            .then(response => {
                if (response.status === 200)
                {
                    setClaim(response.data);
                }
            })
            .catch(err => {
                //TO-DO: Proper Error Handler ...
            })
            .finally(() => setIsLoading(false))
    }

    /**
     * Event Handler for pressing Approval Button. 
     * Changes claim status to "Approval Run". 
     */
    function onApprove(): void
    {
        putClaim.confirmed = true;
        putClaim.status    = ClaimStatus.ApprovalRun;

        setIsLoading(true); // Loading animation while request is send to the server ...
        msalRestClient.put<Claim>("/api/travelclaim", putClaim)
            .then(response => {
                if (response.status === 200)
                {
                    setClaim(response.data);
                }
            })
            .catch(err => {
                //TODO: Proper Error Handler ...
            })
            .finally(() => setIsLoading(false))
    }

    /**
     * 
     * @param recipient
     */
    function onResend(recipient: string): void
    {
        setIsLoading(true); // Loading animation while request ...
        msalRestClient.post("/api/travelclaim/summary", { Recipient: recipient, Guid: claim?.guid })
            .then(response => {
                if (response.status === 200) {
                    // OK Message to User ...
                }
            })
            .catch(err => {
                // TODO: Proper Error Handling ...
            })
            .finally(() => setIsLoading(false))
    }

    /**
     * Method compiles a reject reason string for displaying within the current view.
     * Combines pre-filled reason and other reason text if available.
     */
    function renderRejectReason(): string | undefined {

        let reason: string | undefined = claim?.rejectReason + "\n";

        if (claim?.othertxt)
        {
            reason += ": " + claim?.othertxt;
        }
        return reason;
    }

    /**
     * Method renders a card component that displays all uploaded receipts. 
     */
    function renderFilePane(): React.JSX.Element {
        return (
            <div className="card mt-2">
                <div className="card-header">
                    <i className="bi bi-file-arrow-up-fill me-1"></i>Documents for verification
                </div>
                <div className="card-body">
                    <div className="row">

                        <div className="col-12">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Filename</th>
                                        <th>Category</th>
                                        <th>Upload Timestamp</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        files.map((value, index) => {
                                            return (
                                                <tr id={"file_" + index}>
                                                    <td>{value.id}</td>
                                                    <td><a target="_blank" href={value.url} rel="noreferrer">{value.fileName}</a></td>
                                                    <td>{value.category}</td>
                                                    <td>{new Date(value.created ?? "").toDateString()}</td>
                                                </tr>
                                            )
                                        })
                                    }
                                </tbody>
                            </table>
                        </div>

                    </div>
                </div>
            </div>
        )
    }

    /**
     * Event Handler for deleting a claim.
     */
    const onDeleteAction = (): void =>
    {
        setIsLoading(true);
        msalRestClient.delete("/api/travelclaim/" + claim?.guid)
            .then(response => {
                if (response.status === 200)
                {
                    setClaim(response.data);
                }
            })
            .catch(err => {
                // TODO: Proper Error Handling ...
            })
            .finally(() => setIsLoading(false));
    }

    return (
        <main className="container mb-4">

            <div className="row text-center">
                <div className="col-12">
                    <img src="/brand.png" className="brand" alt="brand" />
                </div>
            </div>

            <div className="row text-center">
                <div className="col-12">
                    <h2>Unit Costs Claim Form</h2>
                </div>
                <div className="col-12">
                    <p>Overview of submitted Unit Cost Form.</p>
                </div>
            </div>

            <div className="row mb-2">
                <div className="col-12">
                    <Link to="/admin/claims" className="btn btn-warning"><i className="bi bi-arrow-left me-2"></i>Back to Overview</Link>
                </div>
            </div>

            {
                isLoading ? <SkeletonCardLoad cardsCount={1} />
                    :
                    <>
                        <div className="card">
                            <div className="row g-0">

                                <div className="col-12 col-md-8">

                                    { /* Render Claim Informations ... */ }
                                    <div className="card-body">

                                        <div className="row">
                                            <div className="col-12">
                                                <ClaimStatusBadge status={claim?.status} />
                                            </div>
                                        </div>

                                        <hr />

                                        <div className="row text-center">
                                            <div className="col-12">
                                                <Tooltip id="reimbursementTTip" />
                                                <label className="display-4 fw-bold" data-tooltip-id="reimbursementTTip" data-tooltip-content="Total Reimbursement">&euro; {(claim?.calculatedTravelCosts?.amount ?? 0) + (claim?.calculatedAccommodationCosts?.totalAmount ?? 0)}</label>
                                            </div>
                                        </div>

                                        <hr />

                                        <dl className="row">
                                            <dt className="col-6 col-md-12">
                                                <label><i className="bi bi-person me-2"></i>Participant</label>
                                            </dt>
                                            <dd className="col-6 col-md-12">
                                                {claim?.lastname + ", " + claim?.firstname}
                                            </dd>
                                        </dl>

                                        <dl className="row">
                                            <dt className="col-6 col-md-12">
                                                <label><i className="bi bi-envelope me-2"></i>Email</label>
                                            </dt>
                                            <dd className="col col-md-12">
                                                {claim?.email}
                                            </dd>
                                        </dl>

                                        <dl className="row">
                                            <dt className="col-6 col-md-12">
                                                <label><i className="bi bi-phone me-2"></i>Phone</label>
                                            </dt>
                                            <dd className="col-6 col-md-12">
                                                {claim?.phone}
                                            </dd>
                                        </dl>

                                        <dl className="row">
                                            <dt className="col-6 col-md-12">
                                                <label><i className="bi bi-arrow-right me-2"></i>Place of employment</label>
                                            </dt>
                                            <dd className="col-6 col-md-12">
                                                {claim?.institution.name} - {claim?.institution.address.street} - {claim?.institution.address.city}
                                            </dd>
                                        </dl>

                                        <dl className="row">
                                            <dt className="col-6 col-md-12">
                                                <label><i className="bi bi-arrow-left me-2"></i>Event Location</label>
                                            </dt>
                                            <dd className="col-6 col-md-12">
                                                {claim?.event.location}
                                            </dd>
                                        </dl>

                                        <hr /> 

                                        <dl className="row">
                                            <dt className="form-group col-12 col-md-4">
                                                <label><i className="bi bi-bank me-2"></i>IBAN</label>
                                            </dt>
                                            <dd className="col-12- col-md-8">
                                                {claim?.elvData?.iban}
                                            </dd>
                                            <dt className="form-group col-12 col-md-4">
                                                <label><i className="bi bi-bank me-2"></i>Bankname</label>
                                            </dt>
                                            <dd className="col-12 col-md-8">
                                                {claim?.elvData?.bankname}
                                            </dd>
                                            <dt className="col-12 col-md-4">
                                                <label><i className="bi bi-bank me-2"></i>BIC</label>
                                            </dt>
                                            <dd className="col-6 col-md-8">
                                                {claim?.elvData?.bic}
                                            </dd>
                                        </dl>

                                        {claim?.elvData?.bankAddress &&
                                            <dl className="row">
                                                <dt className="col-12 col-md-4">
                                                    <label><i className="bi bi-pin me-2"></i>Bankaddress</label>
                                                </dt>
                                                <dd className="col-12 col-md-8">
                                                    {claim?.elvData.bankAddress.street} - {claim?.elvData.bankAddress.postalCode} - {claim?.elvData.bankAddress.city} - {claim?.elvData.bankAddress.country}
                                                </dd>
                                            </dl>
                                        }

                                        <hr />

                                        <dl className="row">
                                            <dt className="form-group col-12 col-md-6">
                                                <label><i className="bi bi-person me-2"></i>Account Holder</label>
                                            </dt>
                                            <dd className="col-6 col-md-6">
                                                {claim?.elvData?.accountHolderName}
                                            </dd>
                                            <dt className="col-12 col-md-6">
                                                <label><i className="bi bi-pin me-2"></i>Account Holder Address</label>
                                            </dt>
                                            <dd className="col-12 col-md-6">
                                                {claim?.elvData?.accountHolderAddress.street} - {claim?.elvData?.accountHolderAddress.postalCode} - {claim?.elvData?.accountHolderAddress.city} - {claim?.elvData?.accountHolderAddress.country}
                                            </dd>
                                        </dl>

                                    </div>

                                </div>

                                <div className="col-12 col-md-4 bg-light py-2 px-4">

                                    { /* Render Calculated Cost Informations + Event Informations */ }

                                    <div className="row mt-1">
                                        <div className="col-12">
                                            <Tooltip id="travelCostTTip" />
                                            <span className="badge bg-dark badge-lg p-2" data-tooltip-id="travelCostTTip" data-tooltip-content={"Distance travelled by " + (claim?.calculatedTravelCosts?.travelType === 0 ? "airplane" : "land transport")} data-tooltip-place="bottom">
                                                <i className={claim?.calculatedTravelCosts?.travelType === 0 ? "bi bi-airplane me-2" : "bi bi-train-front me-2"}></i>
                                                {Math.round(claim?.calculatedTravelCosts?.distance ?? 0)} km | &euro; {claim?.calculatedTravelCosts?.amount}
                                            </span>
                                            {claim?.calculatedAccommodationCosts &&
                                                <>
                                                    <Tooltip id="accommodationCostTTip" />
                                                    <span className="badge bg-dark badge-lg ms-2 p-2" data-tooltip-id="accommodationCostTTip" data-tooltip-content="Nights spend" data-tooltip-place="bottom">
                                                        <i className="bi bi-house me-2"></i>
                                                        {claim.calculatedAccommodationCosts?.nights} | {claim.calculatedAccommodationCosts.amount} &euro;
                                                    </span>
                                                </>
                                            }
                                        </div>
                                    </div>

                                    <hr />

                                    <dl className="row">
                                        <dt className="col-6 col-md-12">
                                            <label>Event Title</label>
                                        </dt>
                                        <dd className="col-6 col-md-12">
                                            {claim?.event.title}
                                        </dd>
                                    </dl>

                                    <dl className="row">
                                        <dt className="col-6 col-md-12">
                                            <label>Event Number</label>
                                        </dt>
                                        <dd className="col-6 col-md-12">
                                            {claim?.event.number}
                                        </dd>
                                    </dl>

                                    <dl className="row">
                                        <dt className="col-6 col-md-12">
                                            <label><i className="bi bi-calendar me-2"></i>Seminar Start Date</label>
                                        </dt>
                                        <dd className="col-6 col-md-12">
                                            {new Date(claim?.event.startDate ?? "").toDateString()}
                                        </dd>
                                    </dl>

                                    <dl className="row">
                                        <dt className="col-6 col-md-12">
                                            <label><i className="bi bi-calendar me-2"></i>Seminar End Date</label>
                                        </dt>
                                        <dd className="col-6 col-md-12">
                                            {new Date(claim?.event.endDate ?? "").toDateString()}
                                        </dd>
                                    </dl>

                                    <dl className="row">
                                        <dt className="col-6 col-md-12">
                                            <label><i className="bi bi-geo me-2"></i>Location</label>
                                        </dt>
                                        <dd className="col-6 col-md-12">
                                            {claim?.event.location}
                                        </dd>
                                    </dl>

                                    <div className="row">
                                        <div className="col-12">
                                            <a href="#" onClick={() => setVersionDetails(!versionDetails)} style={{ textDecoration: "none" }}>
                                                {versionDetails ? "Hide Version Details" : "Show Version Details"}
                                                <i className={versionDetails ? "ms-1 bi bi-caret-up-fill" : "ms-1 bi bi-caret-down-fill"}></i>
                                            </a>
                                        </div>
                                    </div>

                                    <section className={versionDetails ? "" : "d-none"}>

                                    <hr />

                                    <dl className="row">
                                        <dt className="col-6 col-md-12">
                                            <label><i className="bi bi-signpost me-2"></i>Distance Band Version</label>
                                        </dt>
                                        <dd className="col-6 col-md-12">
                                            {claim?.event.distanceBandVersionName}
                                        </dd>
                                    </dl>

                                    <dl className="row">
                                        <dt className="col-6 col-md-12">
                                            <label><i className="bi bi-flag me-2"></i>Country Version</label>
                                        </dt>
                                        <dd className="col-6 col-md-12">
                                            {claim?.event.countryTableVersionName}
                                        </dd>
                                    </dl>

                                    <dl className="row">
                                        <dt className="col-6 col-md-12">
                                            <label><i className="bi bi-house me-2"></i>Accommodation Version</label>
                                        </dt>
                                        <dd className="col-6 col-md-12">
                                            {claim?.event.accommodationVersionName}
                                        </dd>
                                    </dl>

                                    </section>

                                    {(claim?.status === ClaimStatus.Requested && msalHelper.hasAnyRole('Approver', 'Admin')) && 
                                        <>
                                            <hr/>
                                            <div className="row">
                                                <div className="col-12 col-md-6">
                                                    <button className="btn btn-success w-100" onClick={onApprove}><i className="bi bi-check-lg me-2"></i>Approve</button>
                                                </div>
                                                <div className="col-12 col-md-6">
                                                    <RejectReasonDialog onConfirm={onDecline} />
                                                </div>
                                            </div>
                                        </>
                                    }

                                    <hr />

                                    <div className="row">
                                        <div className="col-12">
                                            <ResendDialog onConfirm={onResend} />
                                        </div>
                                    </div>

                                    {(claim?.status === ClaimStatus.Saved || claim?.status === ClaimStatus.Rejected) && 
                                        <div className="row">
                                            <div className="col-12">
                                                <DeleteDialog index={1} title="Delete Claim" description="Are you sure you want to delete this claim? This action cannot be undone." btnClass="btn btn-danger w-100 mt-1" btnIcon="bi bi-trash me-2" btnText="Delete Claim" onConfirm={() => onDeleteAction()} />
                                            </div>
                                        </div>
                                    }

                                </div>

                            </div>
                        </div>

                        {files?.length > 0 && renderFilePane()}

                        <div className="card mt-2">
                            <div className="card-header">
                                <i className="bi bi-database-fill me-1"></i>History Log
                            </div>
                            <div className="card-body">
                                <div className="row">

                                    <div className="col-12">
                                        <table className="table">
                                            <thead>
                                                <tr>
                                                    <td colSpan={3}>
                                                        <label className="mb-2 text-muted">Transactionnumber: <b>{claim?.identifier}</b></label>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <th>Timestamp</th>
                                                    <th>Description</th>
                                                    <th>Username</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {
                                                    claim?.audits.map((value, index) => {
                                                        return (
                                                            <>
                                                                <tr key={index}>
                                                                    <td className={value.action === AuditEvent.Rejected ? "border-bottom-0" : ""}>{new Date(value.timestamp).toDateString()}</td>
                                                                    <td className={value.action === AuditEvent.Rejected ? "border-bottom-0" : ""}>{value.description}</td>
                                                                    <td className={value.action === AuditEvent.Rejected ? "border-bottom-0" : ""}>{value.username?.toLowerCase()}</td>
                                                                </tr>
                                                                {
                                                                    value.action !== AuditEvent.Rejected ? <></> :
                                                                        <tr className="bg-light">
                                                                            <td colSpan={3} className="border-top-0 pt-0">
                                                                                {"Reject Reason: " + renderRejectReason()}
                                                                            </td>
                                                                        </tr>
                                                                }
                                                            </>
                                                        )
                                                    })
                                                }
                                            </tbody>
                                        </table>
                                    </div>
                                    </div>
                            </div>
                        </div>
                    </>
            }

        </main>
    )
}
export default TravelClaim;